/**
 * Firebase Authentication Hook
 *
 * Provides complete authentication functionality with Firebase Auth.
 * This closes the backend authentication gap by providing a full auth solution.
 *
 * Features:
 * - Email/password authentication
 * - Google OAuth
 * - Password reset
 * - Session management
 * - User state management
 * - JWT token generation for API calls
 */

import { useEffect, useState, useCallback } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../config/firebase';

export function useFirebaseAuth() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [idToken, setIdToken] = useState(null);

  // Listen for auth state changes
  useEffect(() => {
    if (!isFirebaseConfigured() || !auth) {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);

      // Get ID token for API calls
      if (user) {
        try {
          const token = await user.getIdToken();
          setIdToken(token);
        } catch (err) {
          console.error('Error getting ID token:', err);
        }
      } else {
        setIdToken(null);
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Sign in with email and password
  const signIn = useCallback(async (email, password) => {
    if (!isFirebaseConfigured() || !auth) {
      throw new Error('Firebase not configured');
    }

    try {
      setError(null);
      const result = await signInWithEmailAndPassword(auth, email, password);
      const token = await result.user.getIdToken();
      setIdToken(token);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Sign up with email and password
  const signUp = useCallback(async (email, password, displayName = null) => {
    if (!isFirebaseConfigured() || !auth) {
      throw new Error('Firebase not configured');
    }

    try {
      setError(null);
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Update display name if provided
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }

      // Send email verification
      await sendEmailVerification(result.user);

      const token = await result.user.getIdToken();
      setIdToken(token);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Sign in with Google
  const signInWithGoogle = useCallback(async () => {
    if (!isFirebaseConfigured() || !auth) {
      throw new Error('Firebase not configured');
    }

    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const token = await result.user.getIdToken();
      setIdToken(token);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Sign out
  const logout = useCallback(async () => {
    if (!isFirebaseConfigured() || !auth) {
      throw new Error('Firebase not configured');
    }

    try {
      setError(null);
      await signOut(auth);
      setIdToken(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (email) => {
    if (!isFirebaseConfigured() || !auth) {
      throw new Error('Firebase not configured');
    }

    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Refresh ID token (call this before API requests if token is old)
  const refreshToken = useCallback(async () => {
    if (!user) return null;

    try {
      const token = await user.getIdToken(true); // Force refresh
      setIdToken(token);
      return token;
    } catch (err) {
      console.error('Error refreshing token:', err);
      return null;
    }
  }, [user]);

  return {
    // User state
    user,
    loading,
    error,
    isAuthenticated: !!user,
    isEmailVerified: user?.emailVerified || false,

    // ID token for API calls
    idToken,
    refreshToken,

    // Auth methods
    signIn,
    signUp,
    signInWithGoogle,
    logout,
    resetPassword,

    // Firebase configured status
    isConfigured: isFirebaseConfigured()
  };
}
